import React, { useState, useEffect } from 'react';
import { 
    SyncOutlined, 
    CheckCircleOutlined, 
    ExclamationCircleOutlined,
    WarningOutlined,
    SendOutlined,
    ReloadOutlined,
    MailOutlined
} from '@ant-design/icons';
import { Table, Tag, Button, Modal, Select, Tooltip, Badge, message, Space, Card, Statistic, Row, Col, Alert, Tabs } from 'antd';
import PlatformHealth from './PlatformHealth';
import styles from './styles/sync-monitor.module.scss';

const NOTIFICATION_TYPES = {
    payroll_processing: { label: 'Payroll Processing', color: 'processing' },
    payroll_complete: { label: 'Payroll Complete', color: 'success' },
    time_attendance_complete: { label: 'T&A Complete', color: 'success' },
    direct_deposit_complete: { label: 'DDS Complete', color: 'success' },
    provider_issues: { label: 'Provider Issues', color: 'warning' },
    service_restored: { label: 'Service Restored', color: 'cyan' }
};

const STATUS_CONFIG = {
    IN_PROGRESS: { color: 'processing', icon: <SyncOutlined spin />, label: 'In Progress' },
    COMPLETE: { color: 'success', icon: <CheckCircleOutlined />, label: 'Complete' },
    FAILED: { color: 'error', icon: <ExclamationCircleOutlined />, label: 'Failed' },
    DIRECT_DEPOSIT_SWITCH: { color: 'cyan', icon: <SyncOutlined />, label: 'DDS Pending' },
    EXPIRED: { color: 'default', icon: <ExclamationCircleOutlined />, label: 'Expired' }
};

function SyncMonitor({ data, loading, onRefresh }) {
    const [syncs, setSyncs] = useState([]);
    const [notifyModalOpen, setNotifyModalOpen] = useState(false);
    const [selectedSync, setSelectedSync] = useState(null);
    const [selectedNotificationType, setSelectedNotificationType] = useState(null);
    const [sending, setSending] = useState(false);
    const [resolving, setResolving] = useState(null);

    useEffect(() => {
        if (data?.fetchSyncMonitor) {
            loadSyncs();
        }
    }, [data]);

    const loadSyncs = async () => {
        try {
            const result = await data.fetchSyncMonitor(null, 50);
            setSyncs(result?.syncs || []);
        } 
        catch (error) {
            console.error('Error loading syncs:', error);
            message.error('Failed to load sync data');
        }
    };

    const handleRefresh = async () => {
        await loadSyncs();
        if (onRefresh) onRefresh();
    };

    const handleOpenNotifyModal = (sync) => {
        setSelectedSync(sync);
        setSelectedNotificationType(sync.has_errors ? 'provider_issues' : 'payroll_complete');
        setNotifyModalOpen(true);
    };

    const handleSendNotification = async () => {
        if (!selectedSync || !selectedNotificationType) return;

        setSending(true);
        try {
            await data.sendSyncNotification(
                selectedSync.client_id,
                selectedNotificationType,
                selectedSync.link_token_id
            );
            message.success('Notification sent successfully');
            setNotifyModalOpen(false);
        } 
        catch (error) {
            message.error('Failed to send notification');
        } 
        finally {
            setSending(false);
        }
    };

    const handleResolveTicket = async (ticketId, sendNotification = false) => {
        setResolving(ticketId);
        try {
            await data.resolveTroubleTicket(ticketId, sendNotification);
            message.success(sendNotification ? 'Ticket resolved and notification sent' : 'Ticket resolved');
            await loadSyncs();
        } 
        catch (error) {
            message.error('Failed to resolve ticket');
        } 
        finally {
            setResolving(null);
        }
    };

    // Statistics - exclude expired syncs from "In Progress" count
    const inProgressCount = syncs.filter(s => 
        s.status === 'IN_PROGRESS' && !s.is_link_token_expired
    ).length;
    const errorCount = syncs.filter(s => s.has_errors).length;
    const openTicketsCount = syncs.reduce((acc, s) => 
        acc + (s.trouble_tickets?.filter(t => t.status === 'open')?.length || 0), 0
    );

    const columns = [
        {
            title: 'User',
            key: 'user',
            render: (_, record) => (
                <div className={styles.userCell}>
                    <div className={styles.userName}>{record.account_name || 'Unknown'}</div>
                    <div className={styles.userEmail}>{record.account_email || record.client_id}</div>
                </div>
            ),
            width: 200
        },
        {
            title: 'Platform',
            dataIndex: 'platform_name',
            key: 'platform_name',
            render: (platformName) => platformName || <span style={{ color: '#999' }}>—</span>,
            width: 150
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status, record) => {
                // If link token is expired, show as "Expired" regardless of status
                if (record.is_link_token_expired && status === 'IN_PROGRESS') {
                    const config = STATUS_CONFIG.EXPIRED;
                    return (
                        <Tag icon={config.icon} color={config.color}>
                            {config.label}
                        </Tag>
                    );
                }
                const config = STATUS_CONFIG[status] || { color: 'default', label: status };
                return (
                    <Tag icon={config.icon} color={config.color}>
                        {config.label}
                    </Tag>
                );
            },
            width: 140
        },
        {
            title: 'Sync Data',
            key: 'webhookStatus',
            render: (_, record) => {
                const webhooks = ['employment', 'income', 'paystubs', 'shifts', 'identity'];
                return (
                    <div className={styles.webhookStatus}>
                        {webhooks.map(key => {
                            const data = record[key];
                            if (!data) return null;
                            
                            const isError = data.status === 'dataNotAvailable' || data.status === 'error';
                            const isSuccess = data.status === 'success';
                            
                            return (
                                <Tooltip key={key} title={`${key}: ${data.status || 'pending'}${data.error_message ? ` - ${data.error_message}` : ''}`}>
                                    <span className={`${styles.webhookDot} ${isError ? styles.error : isSuccess ? styles.success : styles.pending}`}>
                                        {key.charAt(0).toUpperCase()}
                                    </span>
                                </Tooltip>
                            );
                        })}
                    </div>
                );
            },
            width: 180
        },
        {
            title: 'Issues',
            key: 'issues',
            render: (_, record) => {
                if (!record.has_errors && (!record.trouble_tickets || record.trouble_tickets.length === 0)) {
                    return <Tag color="success">None</Tag>;
                }

                const openTickets = record.trouble_tickets?.filter(t => t.status === 'open') || [];
                
                return (
                    <div className={styles.issuesCell}>
                        {record.has_errors && (
                            <Tooltip title={record.error_summary?.join(', ')}>
                                <Tag icon={<WarningOutlined />} color="warning">
                                    Data Issues
                                </Tag>
                            </Tooltip>
                        )}
                        {openTickets.length > 0 && (
                            <Badge count={openTickets.length} size="small">
                                <Tag icon={<ExclamationCircleOutlined />} color="error">
                                    Tickets
                                </Tag>
                            </Badge>
                        )}
                    </div>
                );
            },
            width: 150
        },
        {
            title: 'Created',
            dataIndex: 'created_at',
            key: 'created_at',
            render: (date) => new Date(date).toLocaleString(),
            width: 160
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Send Notification">
                        <Button 
                            type="text" 
                            icon={<MailOutlined />} 
                            onClick={() => handleOpenNotifyModal(record)}
                        />
                    </Tooltip>
                    {record.trouble_tickets?.filter(t => t.status === 'open').map(ticket => (
                        <Tooltip key={ticket.id} title="Resolve & Notify">
                            <Button
                                type="text"
                                icon={<CheckCircleOutlined />}
                                loading={resolving === ticket.id}
                                onClick={() => handleResolveTicket(ticket.id, true)}
                            />
                        </Tooltip>
                    ))}
                </Space>
            ),
            width: 120
        }
    ];

    const expandedRowRender = (record) => {
        if (!record.trouble_tickets || record.trouble_tickets.length === 0) {
            return <Alert message="No trouble tickets for this sync" type="info" showIcon />;
        }

        const ticketColumns = [
            { title: 'Name', dataIndex: 'name', key: 'name' },
            { title: 'Outcome', dataIndex: 'outcome', key: 'outcome', ellipsis: true },
            { title: 'Error Code', dataIndex: 'error_code', key: 'error_code' },
            { 
                title: 'Status', 
                dataIndex: 'status', 
                key: 'status',
                render: (status) => (
                    <Tag color={status === 'open' ? 'error' : 'success'}>
                        {status}
                    </Tag>
                )
            },
            { 
                title: 'Created', 
                dataIndex: 'created_at', 
                key: 'created_at',
                render: (date) => new Date(date).toLocaleString()
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, ticket) => ticket.status === 'open' ? (
                    <Space>
                        <Button 
                            size="small" 
                            onClick={() => handleResolveTicket(ticket.id, false)}
                            loading={resolving === ticket.id}
                        >
                            Resolve
                        </Button>
                        <Button 
                            size="small" 
                            type="primary"
                            onClick={() => handleResolveTicket(ticket.id, true)}
                            loading={resolving === ticket.id}
                        >
                            Resolve & Notify
                        </Button>
                    </Space>
                ) : null
            }
        ];

        return (
            <Table
                columns={ticketColumns}
                dataSource={record.trouble_tickets}
                rowKey="id"
                pagination={false}
                size="small"
            />
        );
    };

    return (
        <div className={styles.syncMonitor}>
            <Tabs
                defaultActiveKey="syncs"
                items={[
                    {
                        key: 'syncs',
                        label: `Sync Monitor`,
                        children: (
                            <>
                                <div className={styles.header}>
                                    <h2>Sync Monitor</h2>
                                    <Button 
                                        icon={<ReloadOutlined />} 
                                        onClick={handleRefresh}
                                        loading={loading}
                                    >
                                        Refresh
                                    </Button>
                                </div>

                                <Row gutter={16} className={styles.statsRow}>
                                    <Col span={8}>
                                        <Card>
                                            <Statistic
                                                title="In Progress"
                                                value={inProgressCount}
                                                prefix={<SyncOutlined spin={inProgressCount > 0} />}
                                                valueStyle={{ color: inProgressCount > 0 ? '#1890ff' : '#52c41a' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card>
                                            <Statistic
                                                title="With Errors"
                                                value={errorCount}
                                                prefix={<WarningOutlined />}
                                                valueStyle={{ color: errorCount > 0 ? '#faad14' : '#52c41a' }}
                                            />
                                        </Card>
                                    </Col>
                                    <Col span={8}>
                                        <Card>
                                            <Statistic
                                                title="Open Tickets"
                                                value={openTicketsCount}
                                                prefix={<ExclamationCircleOutlined />}
                                                valueStyle={{ color: openTicketsCount > 0 ? '#ff4d4f' : '#52c41a' }}
                                            />
                                        </Card>
                                    </Col>
                                </Row>

                                <Table
                                    columns={columns}
                                    dataSource={syncs}
                                    rowKey="link_token_id"
                                    loading={loading}
                                    expandable={{
                                        expandedRowRender,
                                        rowExpandable: (record) => record.trouble_tickets?.length > 0
                                    }}
                                    pagination={{ pageSize: 10 }}
                                    scroll={{ x: 950 }}
                                />

                                <Modal
                                    title="Send Notification"
                                    open={notifyModalOpen}
                                    onCancel={() => setNotifyModalOpen(false)}
                                    onOk={handleSendNotification}
                                    confirmLoading={sending}
                                    okText="Send"
                                >
                                    <div className={styles.notifyModal}>
                                        <p>
                                            Send a notification to <strong>{selectedSync?.account_email || selectedSync?.client_id}</strong>
                                        </p>
                                        <Select
                                            style={{ width: '100%' }}
                                            value={selectedNotificationType}
                                            onChange={setSelectedNotificationType}
                                            options={Object.entries(NOTIFICATION_TYPES).map(([key, config]) => ({
                                                value: key,
                                                label: (
                                                    <span>
                                                        <Tag color={config.color} style={{ marginRight: 8 }}>{config.label}</Tag>
                                                    </span>
                                                )
                                            }))}
                                        />
                                        {selectedNotificationType === 'provider_issues' && (
                                            <Alert
                                                style={{ marginTop: 16 }}
                                                message="This will notify the user that their provider is experiencing issues."
                                                type="warning"
                                                showIcon
                                            />
                                        )}
                                        {selectedNotificationType === 'service_restored' && (
                                            <Alert
                                                style={{ marginTop: 16 }}
                                                message="This will notify the user that service has been restored."
                                                type="success"
                                                showIcon
                                            />
                                        )}
                                    </div>
                                </Modal>
                            </>
                        )
                    },
                    {
                        key: 'platform-health',
                        label: 'Platform Health',
                        children: <PlatformHealth data={data} loading={loading} />
                    }
                ]}
            />
        </div>
    );
}

export default SyncMonitor;
